



import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom"; 
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { 
  Search, Plus, Trash2, FileText, User, X, Upload, 
  Calendar, CheckCircle, Loader2, ArrowLeft, RotateCcw 
} from "lucide-react";

const MemberDocuments = () => {
  const navigate = useNavigate(); 
  const [searchParams] = useSearchParams();
  const directId = searchParams.get("id");

  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploadData, setUploadData] = useState({ name: "", file: null });
  const [isUploading, setIsUploading] = useState(false);

  // --- STYLES ---
  const styles = {
    container: { padding: "40px", backgroundColor: "#f4f7fe", minHeight: "calc(100vh - 70px)", fontFamily: "'Inter', sans-serif" },
    backBtn: { background: '#fff', border: '1px solid #e2e8f0', padding: '10px 20px', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', color: '#475569', marginBottom: '20px' },
    searchCard: { backgroundColor: "#fff", padding: "24px", borderRadius: "20px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)", marginBottom: "30px" },
    searchGroup: { display: "flex", gap: "10px", position: "relative", maxWidth: "750px" }, 
    input: { flex: 1, padding: "12px 15px 12px 45px", borderRadius: "12px", border: "1.5px solid #e2e8f0", outline: "none" },
    primaryBtn: { backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "0 25px", borderRadius: "12px", cursor: "pointer", fontWeight: "700", display: 'flex', alignItems: 'center', gap: '8px' },
    btnGhost: { backgroundColor: "#fff", color: "#64748b", padding: "0 20px", borderRadius: "12px", border: "1.5px solid #e2e8f0", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" },
    memberDetailsCard: { backgroundColor: "#fff", borderRadius: "24px", padding: "30px", boxShadow: "0 10px 30px rgba(0,0,0,0.04)", marginBottom: "30px" },
    uploadSection: { backgroundColor: "#f8fafc", padding: "25px", borderRadius: "20px", border: "1px dashed #cbd5e1", marginBottom: "30px" },
    resultsList: { position: "absolute", top: "55px", left: 0, right: 0, backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 10px 25px rgba(0,0,0,0.1)", zIndex: 100, border: "1px solid #e2e8f0" },
    resultItem: { padding: "12px 20px", borderBottom: "1px solid #f1f5f9", cursor: "pointer", display: "flex", justifyContent: "space-between" }
  };

  // --- Logic for Fetching Documents (Defined first to avoid reference errors) ---
  const fetchDocuments = useCallback(async (memberId) => {
    setDocsLoading(true);
    try {
      const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_DOC_GET_BY_MEMBER}/${memberId}`);
      const data = await res.json();
      setDocuments(data || []);
    } catch (err) { console.error(err); }
    finally { setDocsLoading(false); }
  }, []);

  // --- Logic for Fetching Direct ID from URL ---
  useEffect(() => {
    if (directId) {
        fetch(`${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_BASE}/${directId}`)
            .then(res => res.json())
            .then(data => {
                setSelectedMember(data);
                fetchDocuments(data.memberId);
            });
    }
  }, [directId, fetchDocuments]);

  const searchMembers = async () => {
    if (!searchTerm.trim()) return;
    setIsSearching(true);
    try {
      const url = `${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_GET_ALL}?pageNumber=1&pageSize=5&searchTerm=${searchTerm}`;
      const response = await fetch(url);
      const result = await response.json();
      setMembers(result.data || []);
    } catch (e) { console.error(e); }
    finally { setIsSearching(false); }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setMembers([]);
  };

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setMembers([]);
    setSearchTerm("");
    fetchDocuments(member.memberId);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file || !uploadData.name) return Swal.fire("Required", "Provide details", "warning");

    setIsUploading(true);
    const formData = new FormData();
    formData.append("MemberId", selectedMember.memberId);
    formData.append("DocumentName", uploadData.name);
    formData.append("File", uploadData.file);

    try {
      const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_DOC_UPLOAD}`, { method: "POST", body: formData });
      if (res.ok) {
        Swal.fire("Success", "Uploaded", "success");
        setUploadData({ name: "", file: null });
        document.getElementById("fileInput").value = "";
        fetchDocuments(selectedMember.memberId);
      }
    } catch (err) { Swal.fire("Error", "Failed", "error"); }
    finally { setIsUploading(false); }
  };

  const handleDelete = async (docId) => {
    const result = await Swal.fire({ title: 'Delete?', icon: 'warning', showCancelButton: true });
    if (result.isConfirmed) {
        await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_DOC_DELETE}/${docId}`, { method: "DELETE" });
        fetchDocuments(selectedMember.memberId);
    }
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        
        <div style={{ marginBottom: "30px" }}>
          <h2 style={{ fontSize: "28px", fontWeight: "800", color: "#1b2559", margin: 0 }}>Add Documents </h2>
        </div>

        {!selectedMember && (
            <div style={styles.searchCard}>
                <div style={styles.searchGroup}>
                    <Search size={18} style={{ position: "absolute", left: "15px", top: "14px", color: "#94a3b8" }} />
                    <input style={styles.input} placeholder="Search member by name, phone or plot..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchMembers()} />
                    
                    <button onClick={searchMembers} style={styles.primaryBtn}> 
                      {isSearching ? <Loader2 className="animate-spin" size={18} /> : "Find Member"} 
                    </button>

                    {searchTerm && (
                      <button onClick={handleClearSearch} style={styles.btnGhost}>
                        <RotateCcw size={16}/> Clear
                      </button>
                    )}

                    {members.length > 0 && (
                        <div style={styles.resultsList}>
                            {members.map(m => (
                                <div key={m.memberId} style={styles.resultItem} onClick={() => handleSelectMember(m)}>
                                    <div>
                                        <div style={{fontWeight: "700", color: "#1b2559"}}>{m.memberName}</div>
                                        <div style={{fontSize: "12px", color: "#a3aed0"}}>{m.phoneNo} | Plot: {m.plotNumbers || 'No Plot Assigned'}</div>
                                    </div>
                                    <Plus size={16} color="#4f46e5" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        )}

        {selectedMember && (
          <div style={styles.memberDetailsCard}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "25px" }}>
                <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
                    <div style={{ width: "50px", height: "50px", backgroundColor: "#eef2ff", borderRadius: "15px", display: "flex", justifyContent: "center", alignItems: "center", color: "#4f46e5" }}><User size={24} /></div>
                    <div>
                        <h3 style={{ margin: 0, color: "#1b2559", fontSize: "24px", fontWeight: "800" }}>{selectedMember.memberName}</h3>
                        <span style={{ fontSize: "15px", color: "#6366f1", fontWeight: "600" }}>
                            {selectedMember.phoneNo} • {selectedMember.plotNumbers ? `Plot #${selectedMember.plotNumbers}` : 'No Plot Details Found'}
                        </span>
                    </div>
                </div>
                {!directId && <button onClick={() => setSelectedMember(null)} style={{ background: "#fef2f2", color: "#ef4444", border: "none", padding: "8px 15px", borderRadius: "10px", cursor: "pointer", fontWeight: "700" }}>Change Member</button>}
            </div>

            <div style={styles.uploadSection}>
                <form onSubmit={handleUpload} style={{ display: "flex", gap: "15px" }}>
                  <input type="text" placeholder="Document Name (e.g. Agreement)" style={{ ...styles.input, flex: "2", paddingLeft: "15px" }} value={uploadData.name} onChange={(e) => setUploadData({...uploadData, name: e.target.value})} />
                  <input id="fileInput" type="file" style={{ ...styles.input, flex: "2", padding: "10px" }} onChange={(e) => setUploadData({...uploadData, file: e.target.files[0]})} />
                  <button type="submit" style={styles.primaryBtn} disabled={isUploading}>{isUploading ? <Loader2 className="animate-spin" size={18} /> : "Upload Now"}</button>
                </form>
            </div>

            {docsLoading ? (
               <div style={{textAlign: 'center', padding: '20px'}}><Loader2 className="animate-spin" size={24} /> Loading documents...</div>
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "20px" }}>
                    {documents.map((doc) => (
                        <div key={doc.documentId} style={{ backgroundColor: "#fff", padding: "20px", borderRadius: "16px", border: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                                <div style={{ fontWeight: "700", color: "#1b2559", fontSize: "14px" }}>{doc.documentName}</div>
                                <div style={{ fontSize: "11px", color: "#a3aed0" }}>{new Date(doc.uploadDate).toLocaleDateString()}</div>
                            </div>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <a href={`${APIConfig.APIACTIVEURL.replace('/api', '')}/${doc.filePath}`} target="_blank" rel="noreferrer" style={{ padding: "8px", background: "#f0f9ff", color: "#0ea5e9", borderRadius: "8px" }}><CheckCircle size={16} /></a>
                                <button onClick={() => handleDelete(doc.documentId)} style={{ border: "none", padding: "8px", background: "#fef2f2", color: "#ef4444", borderRadius: "8px", cursor: "pointer" }}><Trash2 size={16} /></button>
                            </div>
                        </div>
                    ))}
                    {documents.length === 0 && <div style={{gridColumn: '1/-1', textAlign: 'center', color: '#a3aed0', padding: '40px'}}>No documents uploaded yet.</div>}
                </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default MemberDocuments;